import { Link } from "react-router-dom";
import styled from "styled-components";
import { LogoSVG } from "../../../../assets/svg/SvgIcons";
import { currentYear } from "../../../../utils/helpers";
import { URL_BOOKINGS, URL_LOGIN } from "../../../../utils/urls";

const quickLinks = [
  { label: "Home", href: "#home" },
  { label: "Services", href: "#services" },
  { label: "Payments", href: "#payments" },
  { label: "Contact", href: "#contact-us" },
];

const portals = [
  {
    title: "Public Users",
    description: "Find and reserve parking in minutes",
    to: `${URL_BOOKINGS}/create`,
  },
  {
    title: "Parking Agents",
    description: "Sign in for field and operations workflows",
    to: URL_LOGIN,
  },
];

const Footer = () => {
  return (
    <FooterWrapper>
      <div className="container">
        <FooterContent>
          <BrandColumn>
            <LogoRow>
              <img
                src={LogoSVG}
                height={48}
                width={48}
                alt="Smart Parking logo"
              />
              <h2>SMART Parking</h2>
            </LogoRow>
            <p>
              Smart Street Parking by YOYA Technologies helps cities run parking
              operations with better visibility, stronger collections, and
              cleaner user experiences.
            </p>
          </BrandColumn>

          <Column>
            <h3>Quick Navigation</h3>
            <ul>
              {quickLinks.map((link) => (
                <li key={link.label}>
                  <a href={link.href}>{link.label}</a>
                </li>
              ))}
            </ul>
          </Column>

          <Column>
            <h3>User Portals</h3>
            <PortalList>
              {portals.map((portal) => (
                <li key={portal.title}>
                  <Link to={portal.to}>
                    <strong>{portal.title}</strong>
                    <span>{portal.description}</span>
                  </Link>
                </li>
              ))}
            </PortalList>
          </Column>

          <Column>
            <h3>Contact</h3>
            <ContactText>
              <a href="mailto:info@yoyatechnologies.com">
                info@yoyatechnologies.com
              </a>
              <a href="tel:+256783870616">+256 783 870616</a>
            </ContactText>
          </Column>
        </FooterContent>
      </div>

      <CopyrightSection>
        <div className="container copy-row">
          <p>
            Copyright {currentYear()} SMART Parking. All rights reserved. Built
            by{" "}
            <a
              href="https://www.yoyatechnologies.com"
              target="_blank"
              rel="noopener noreferrer"
              style={{
                color: "var(--lp-primary)",
                textDecoration: "underline",
              }}
            >
              YOYA Technologies
            </a>
            .
          </p>
        </div>
      </CopyrightSection>
    </FooterWrapper>
  );
};

export default Footer;

/* Light footer: the closing call-to-action above it is the dark block, so
   stacking a second dark band here would flatten the end of the page. */
const FooterWrapper = styled.footer`
  background: var(--lp-bg-alt);
  border-top: 1px solid var(--lp-border);
  color: var(--lp-text);
`;

const FooterContent = styled.div`
  padding: 64px 0 40px;
  display: grid;
  grid-template-columns: minmax(0, 1.35fr) repeat(3, minmax(0, 1fr));
  gap: 28px;

  @media (max-width: 1024px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  @media (max-width: 760px) {
    grid-template-columns: 1fr;
    padding: 46px 0 30px;
  }
`;

const BrandColumn = styled.section`
  display: grid;
  gap: 14px;
  align-content: start;

  p {
    margin: 0;
    max-width: 46ch;
    color: var(--lp-muted);
    line-height: 1.7;
    font-size: 0.9rem;
  }
`;

const LogoRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;

  h2 {
    margin: 0;
    color: var(--lp-text);
    font-size: 1.3rem;
    font-weight: 700;
  }
`;

const Column = styled.section`
  h3 {
    margin: 0 0 14px;
    color: var(--lp-text);
    font-size: 0.95rem;
    font-weight: 600;
  }

  ul {
    list-style: none;
    padding: 0;
    margin: 0;
    display: grid;
    gap: 10px;
  }

  li {
    margin: 0;
  }

  a {
    color: var(--lp-muted);
    font-size: 0.9rem;
    text-decoration: none;
    line-height: 1.5;

    &:hover {
      color: var(--lp-primary);
    }
  }
`;

const PortalList = styled.ul`
  li a {
    border-radius: var(--lp-r-md);
    border: 1px solid var(--lp-border);
    background: #fff;
    padding: 12px 14px;
    display: grid;
    gap: 3px;
    text-decoration: none;

    strong {
      color: var(--lp-text);
      font-size: 0.88rem;
      font-weight: 600;
    }

    span {
      color: var(--lp-muted);
      font-size: 0.79rem;
      line-height: 1.45;
    }

    &:hover {
      border-color: var(--lp-primary);
    }
  }
`;

const ContactText = styled.div`
  display: grid;
  gap: 8px;

  a {
    color: var(--lp-muted);
    font-size: 0.9rem;
    line-height: 1.5;
    text-decoration: none;

    &:hover {
      color: var(--lp-primary);
    }
  }
`;

const CopyrightSection = styled.div`
  border-top: 1px solid var(--lp-border);

  .copy-row {
    padding: 18px 0 22px;
  }

  p {
    margin: 0;
    color: var(--lp-muted);
    font-size: 0.8rem;
    line-height: 1.5;
  }
`;
